import React, { useMemo } from 'react';

import ProtectedData, { isProtectedData } from 'pages/item/v2/components/ProtectedData';

import useStyles from './styles';

interface Props {
    scale?: number;
    score: number | string;
    className?: string;
}

enum Criticality {
    LOW,
    MEDIUM,
    HIGH,
    CRITICAL
}

const RANGES = [
    { name: Criticality.LOW, value: [0, 2.5] },
    { name: Criticality.MEDIUM, value: [2.5, 5] },
    { name: Criticality.HIGH, value: [5, 7.5] },
    { name: Criticality.CRITICAL, value: [7.5, 10] }
];

const ScoreIndicator: React.FC<Props> = ({ score, scale = 1, className }) => {
    const { classes, cx } = useStyles();

    const value = useMemo(() => {
        switch (true) {
            case isProtectedData(score):
                return score;
            case typeof score === 'number' && Number.isFinite(score):
            case typeof score === 'string' && Number.isFinite(Number(score)):
                return Number(Number(score).toFixed(3));
            default:
                return '';
        }
    }, [score]);

    const range = useMemo(
        () =>
            typeof value === 'number'
                ? RANGES.find(({ value: [min, max] }) => value >= min * scale && value <= max * scale) ?? null
                : null,
        [scale, value]
    );

    return (
        <div
            className={cx(
                classes.root,
                {
                    [classes._low]: range?.name === Criticality.LOW,
                    [classes._medium]: range?.name === Criticality.MEDIUM,
                    [classes._high]: range?.name === Criticality.HIGH,
                    [classes._critical]: range?.name === Criticality.CRITICAL
                },
                className
            )}
        >
            <div className={classes.dots}>
                {RANGES.map(({ name }, index) => (
                    <span
                        key={name}
                        className={cx(classes.dot, {
                            [classes._highlighted]: range && index <= RANGES.indexOf(range)
                        })}
                    />
                ))}
            </div>
            <ProtectedData data={value}>
                {({ content }) => <span className={classes.score}>{content}</span>}
            </ProtectedData>
        </div>
    );
};

export default ScoreIndicator;
